/**
 * components/TabularProcessingView.jsx
 *
 * Processing view for Tabular Scan — mirrors the visual style of
 * FileProgressCard / BatchProcessingView but is tuned for the
 * 5-stage tabular pipeline (Preprocess → OCR → BOM Extract →
 * Build Plans → Generate Workbook).
 *
 * On completion the component shows a prominent "Download Inspection Plan"
 * button instead of navigating to a results table (the deliverable IS the
 * Excel workbook).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import API from '../services/api';
import ResultsTable from './ResultsTable';
import TabularResultsView from './TabularResultsView';
import '../styles/components.css';

/* ── Tabular pipeline steps ──────────────────────────────────── */
const TABULAR_STEPS = [
  {
    code:      'PREPROCESS',
    label:     'Preparing Your Drawing',
    desc:      'Loading and preprocessing the assembly drawing image',
    threshold: 15,
    doneMsg:   'Drawing ready for analysis.',
  },
  {
    code:      'OCR',
    label:     'Reading Text & Dimensions',
    desc:      'Extracting all text, part numbers and annotations via OCR',
    threshold: 35,
    doneMsg:   'Text and annotations captured.',
  },
  {
    code:      'GEMINI_BOM',
    label:     'Extracting BOM Table',
    desc:      'Identifying and parsing the variant table with Gemini AI',
    threshold: 60,
    doneMsg:   'BOM table parsed successfully.',
  },
  {
    code:      'BUILD_PLANS',
    label:     'Building Inspection Plans',
    desc:      'Generating one inspection plan per assembly variant',
    threshold: 85,
    doneMsg:   'All variant plans built.',
  },
  {
    code:      'WRITE_EXCEL',
    label:     'Generating Workbook',
    desc:      'Creating Summary + plan sheets in the Excel workbook',
    threshold: 100,
    doneMsg:   'Workbook ready to download.',
  },
];

const ASSUMED_MS = 240_000;   // 4 min assumed total pipeline time

/* Time-based progress interpolation (keeps bar moving even before API responds) */
const KF = [[0,3],[0.10,15],[0.28,35],[0.50,60],[0.75,85],[1,95]];
function interpProgress(t) {
  const ct = Math.min(1, Math.max(0, t));
  for (let i = 1; i < KF.length; i++) {
    if (ct <= KF[i][0]) {
      const r = (ct - KF[i-1][0]) / (KF[i][0] - KF[i-1][0]);
      return KF[i-1][1] + r * (KF[i][1] - KF[i-1][1]);
    }
  }
  return 95;
}

function getStepStatuses(progress, cardStatus) {
  if (cardStatus === 'completed') return TABULAR_STEPS.map(() => 'completed');
  const ai = TABULAR_STEPS.findIndex(s => progress <= s.threshold);
  const activeIdx = ai === -1 ? TABULAR_STEPS.length - 1 : ai;
  return TABULAR_STEPS.map((_, i) => {
    if (cardStatus === 'failed' && i === activeIdx) return 'error';
    if (i < activeIdx)   return 'completed';
    if (i === activeIdx) return cardStatus === 'processing' ? 'running' : 'pending';
    return 'pending';
  });
}

function formatElapsed(ms) {
  if (!ms) return '—';
  const s = Math.max(1, Math.round(ms / 1000));
  if (s < 60) return s + 's';
  return Math.floor(s / 60) + 'm ' + (s % 60) + 's';
}

/* ── SVG icons ───────────────────────────────────────────────── */
function CheckIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}
function XIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}
function DownloadIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );
}

/* ══ Main component ══════════════════════════════════════════════ */
export default function TabularProcessingView({ fileId, fileName, apiKey, onBack, onViewResults }) {
  const [progress,      setProgress]      = useState(3);
  const [cardStatus,    setCardStatus]    = useState('processing');
  const [elapsedMs,     setElapsedMs]     = useState(0);
  const [failureDetail, setFailureDetail] = useState(null);
  const [downloading,   setDownloading]   = useState(false);
  const [variantCount,  setVariantCount]  = useState(null);

  // v2 per-part results
  const [variants,        setVariants]        = useState(null);   // [{index, part_no, final_plan, has_excel, has_pdf}]
  const [selectedIdx,     setSelectedIdx]     = useState(0);
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [showResults,     setShowResults]     = useState(false);
  const [variantsError,   setVariantsError]   = useState(null);
  const [zipBusy,         setZipBusy]         = useState(null);    // 'pdf' | 'excel' | null

  const startTimeRef  = useRef(Date.now());
  const pollRef       = useRef(null);
  const timerRef      = useRef(null);
  const tickerRef     = useRef(null);
  const doneRef       = useRef(false);
  const statusRef     = useRef('processing');
  const progressHigh  = useRef(3);

  const finish = useCallback((finalStatus, msg) => {
    if (doneRef.current) return;
    doneRef.current   = true;
    statusRef.current = finalStatus;
    clearInterval(pollRef.current);
    clearInterval(timerRef.current);
    clearInterval(tickerRef.current);
    setElapsedMs(Date.now() - startTimeRef.current);
    setCardStatus(finalStatus);

    if (finalStatus === 'completed') {
      // Animate bar to 100%
      const startPct = progressHigh.current;
      const t0 = Date.now();
      const anim = setInterval(() => {
        const t   = Math.min(1, (Date.now() - t0) / 1200);
        const cur = Math.round(startPct + (100 - startPct) * t);
        progressHigh.current = cur;
        setProgress(cur);
        if (cur >= 100) clearInterval(anim);
      }, 30);
    } else {
      setProgress(progressHigh.current);
      if (msg) setFailureDetail(msg);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    // Elapsed-time counter
    timerRef.current = setInterval(() => {
      if (statusRef.current !== 'processing') { clearInterval(timerRef.current); return; }
      setElapsedMs(Date.now() - startTimeRef.current);
    }, 1000);

    // Time-based progress ticker — keeps bar moving visually
    tickerRef.current = setInterval(() => {
      if (statusRef.current !== 'processing') { clearInterval(tickerRef.current); return; }
      const timePct = interpProgress((Date.now() - startTimeRef.current) / ASSUMED_MS);
      setProgress(p => Math.max(p, Math.min(94, Math.max(timePct, progressHigh.current))));
    }, 300);

    const poll = async () => {
      if (!mounted || statusRef.current !== 'processing') return;
      try {
        const resp = await API.getProcessStatus(fileId);
        if (!mounted) return;
        if (resp.progress !== undefined) {
          progressHigh.current = Math.max(progressHigh.current, resp.progress);
          setProgress(p => Math.max(p, resp.progress));
        }
        // Extract variant count from message if available
        if (resp.message) {
          const m = resp.message.match(/(\d+)\s+variant/i);
          if (m) setVariantCount(parseInt(m[1], 10));
        }
        if (resp.status === 'completed') {
          finish('completed');
        } else if (resp.status === 'failed') {
          finish('failed', resp.message || resp.error_message || 'Processing failed');
        }
      } catch { /* transient — keep polling */ }
    };

    const run = async () => {
      // Start the tabular pipeline
      try {
        await API.startTabularScan(fileId, apiKey);
      } catch (err) {
        if (mounted) finish('failed', err?.error || 'Failed to start tabular scan');
        return;
      }
      if (!mounted) return;
      // Poll every 3 s
      pollRef.current = setInterval(poll, 3000);
    };

    run();

    return () => {
      mounted = false;
      clearInterval(pollRef.current);
      clearInterval(timerRef.current);
      clearInterval(tickerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await API.downloadTabularExcel(
        fileId,
        fileName.replace(/\.pdf$/i, '') + ' - Tabular Inspection Plan.xlsx',
      );
    } catch (err) {
      setFailureDetail(err?.error || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  // On completion, fetch the per-part inspection plans (v2). Falls back
  // silently to the workbook-download CTA if this endpoint is unavailable
  // (e.g. the classic tabular pipeline produced a single workbook).
  useEffect(() => {
    if (cardStatus !== 'completed' || variants !== null) return;
    let alive = true;
    (async () => {
      setVariantsLoading(true);
      setVariantsError(null);
      try {
        const resp = await API.getTabularVariants(fileId);
        if (!alive) return;
        const list = resp?.variants || [];
        setVariants(list);
        if (list.length) setVariantCount(list.length);
      } catch (err) {
        if (alive) setVariantsError(err?.error || 'Could not load per-part results.');
      } finally {
        if (alive) setVariantsLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [cardStatus, fileId, variants]);

  const safePart = (s) => String(s || 'part').replace(/[^A-Za-z0-9 ._-]+/g, '_');

  const handleVariantPdf = async (v) => {
    try {
      await API.downloadTabularVariantPdf(fileId, v.index, `${safePart(v.part_no)} - Inspection Plan.pdf`);
    } catch (err) { setFailureDetail(err?.error || 'PDF download failed'); }
  };
  const handleVariantExcel = async (v) => {
    try {
      await API.downloadTabularVariantExcel(fileId, v.index, `${safePart(v.part_no)} - Inspection Plan.xlsx`);
    } catch (err) { setFailureDetail(err?.error || 'Excel download failed'); }
  };
  const handleZip = async (type) => {
    setZipBusy(type);
    try { await API.downloadTabularZip(fileId, type); }
    catch (err) { setFailureDetail(err?.error || 'ZIP download failed'); }
    finally { setZipBusy(null); }
  };

  const isProcessing = cardStatus === 'processing';
  const isCompleted  = cardStatus === 'completed';
  const isFailed     = cardStatus === 'failed';
  const stepStatuses = getStepStatuses(progress, cardStatus);
  const doneSteps    = stepStatuses.filter(s => s === 'completed').length;

  return (
    <div className="bpv-root">

      {/* ── Strip header (mirrors FileSelectorStrip style) ── */}
      <div className="fps-strip">
        <div className="fps-summary">
          <div className="fps-summary-counts">
            <span className="fps-total">1 file uploaded</span>
            {isProcessing && (
              <span className="fps-count processing">
                <span className="fps-dot processing" />
                Tabular Scan processing
              </span>
            )}
            {isCompleted && (
              <span className="fps-count completed">
                <span className="fps-dot completed">✓</span>
                Completed
              </span>
            )}
            {isFailed && (
              <span className="fps-count failed">
                <span className="fps-dot failed">✕</span>
                Failed
              </span>
            )}
          </div>
          <button className="fps-cancel-all" onClick={onBack}>
            ← Back to Upload
          </button>
        </div>
        <div className="fps-pills">
          <button className="fps-pill processing active" title={fileName}>
            <span className={`fps-pill-dot ${cardStatus}`}>
              {isCompleted && '✓'}
              {isFailed && '✕'}
            </span>
            <span className="fps-pill-name">{fileName}</span>
          </button>
        </div>

        {isCompleted && (
          <button className="fps-view-results-btn" onClick={handleDownload} disabled={downloading}>
            {downloading ? 'Downloading…' : 'Download Inspection Plan →'}
          </button>
        )}
      </div>

      {/* ── Progress card (mirrors FileProgressCard) ── */}
      <div className={`fpc ${cardStatus}`}>

        {/* Header */}
        <div className="ps-header" style={{ borderRadius: 0 }}>
          <div className="ps-header-left">
            {isProcessing && <span className="ps-live-dot" />}
            {isCompleted  && <span className="ps-done-dot" />}
            {isFailed     && <span className="ps-fail-dot" />}
            <div>
              <h2 className="ps-title">
                {isProcessing ? 'Analysing assembly drawing…'
                 : isCompleted ? 'Tabular scan complete ✓'
                 : 'Tabular scan failed'}
              </h2>
              <p className="ps-subtitle" style={{ wordBreak: 'break-all' }}>{fileName}</p>
              {isFailed && failureDetail && (
                <p className="ps-error-detail" style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--color-danger, #c0392b)', wordBreak: 'break-word' }}>
                  {failureDetail.length > 280 ? failureDetail.slice(0, 280) + '…' : failureDetail}
                </p>
              )}
            </div>
          </div>
          <div className="ps-header-right">
            <span className={`ps-status-chip ${isProcessing ? 'processing' : isCompleted ? 'completed' : 'failed'}`}>
              {isCompleted ? '100%' : Math.round(progress) + '%'}
            </span>
            <span className="ps-step-count">{doneSteps} of {TABULAR_STEPS.length} steps done</span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="ps-progress-track">
          <div
            className={`ps-progress-bar ${isCompleted ? 'complete' : isFailed ? 'failed' : 'animating'}`}
            style={{ width: progress + '%' }}
          />
        </div>

        {/* Step list */}
        <div className="ps-steps-list">
          {TABULAR_STEPS.map((step, i) => {
            const st        = stepStatuses[i];
            const isRunning = st === 'running';
            const isDone    = st === 'completed';
            const isError   = st === 'error';
            const isPending = st === 'pending';

            return (
              <div key={step.code} className={`ps-step-row ${st}`}>
                <div className="ps-step-left">
                  <div className={`ps-step-node ${st}`}>
                    {isDone    && <CheckIcon />}
                    {isError   && <XIcon />}
                    {isRunning && <span className="ps-spin-ring" />}
                    {isPending && <span className="ps-step-num">{i + 1}</span>}
                  </div>
                  {i < TABULAR_STEPS.length - 1 && (
                    <div className={`ps-vert-connector ${isDone ? 'filled' : ''}`} />
                  )}
                </div>

                <div className="ps-step-content">
                  <div className="ps-step-head">
                    <div className="ps-step-title-row">
                      <span className="ps-step-name">{step.label}</span>
                      {isDone    && <span className="ps-step-pill done">Done</span>}
                      {isRunning && <span className="ps-step-pill running">In Progress</span>}
                      {isError   && <span className="ps-step-pill error">Failed</span>}
                      {isPending && <span className="ps-step-pill pending">Waiting</span>}
                    </div>
                    <p className="ps-step-desc-text">{step.desc}</p>
                  </div>

                  {isRunning && (
                    <div className="ps-step-live">
                      <span className="ps-step-live-arrow">→</span>
                      <span className="ps-step-live-msg">{step.desc}</span>
                      <span className="ps-blink-cursor" />
                    </div>
                  )}
                  {isDone && (
                    <div className="ps-step-done-line">
                      <span className="ps-step-done-check">✓</span>
                      <span>{step.doneMsg}</span>
                    </div>
                  )}
                  {isError && (
                    <div className="ps-step-error-line">
                      {failureDetail
                        ? (failureDetail.length > 200 ? failureDetail.slice(0, 200) + '…' : failureDetail)
                        : 'An issue occurred at this step. Please try again.'}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Completion banner */}
        {isCompleted && (
          <div className="ps-completion-banner" style={{ borderRadius: 0 }}>
            <div className="ps-completion-top">
              <span className="ps-completion-icon">✓</span>
              <div className="ps-completion-text">
                <strong>All {TABULAR_STEPS.length} steps completed.</strong>
                <span>
                  {variantCount
                    ? " Your tabular inspection plan is ready — {} variant{} generated.".replace('{}', variantCount).replace('{}', variantCount !== 1 ? 's' : '')
                    : ' Your tabular inspection plan is ready.'}
                </span>
              </div>
            </div>
            <div className="ps-time-stats">
              <div className="ps-time-stat ai">
                <span className="ps-time-stat-icon">⚡</span>
                <div>
                  <div className="ps-time-stat-value">{formatElapsed(elapsedMs)}</div>
                  <div className="ps-time-stat-label">AI completed in</div>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Process complete → "View Results" button, then the full result page
          (like the normal scan). */}
      {isCompleted && variantsLoading && (
        <div className="bpv-results-cta">
          <p className="bpv-results-cta-label">Preparing per-part inspection plans…</p>
        </div>
      )}

      {isCompleted && variants && variants.length > 0 && (
        <div className="bpv-results-cta">
          <p className="bpv-results-cta-label">
            {variants.length} part{variants.length !== 1 ? 's' : ''} ready — each with its own inspection plan.
          </p>
          <button
            className="bpv-view-results-btn"
            onClick={() => (onViewResults ? onViewResults(variants) : setShowResults(true))}
          >
            View Results <span className="bpv-view-results-arrow">→</span>
          </button>
        </div>
      )}

      {/* Fallback inline render when used without a navigation handler. */}
      {isCompleted && variants && variants.length > 0 && showResults && !onViewResults && (
        <TabularResultsView fileId={fileId} variants={variants} />
      )}

      {/* Fallback: workbook download (classic pipeline or variants unavailable) */}
      {isCompleted && (!variants || variants.length === 0) && !variantsLoading && (
        <div className="bpv-results-cta">
          <p className="bpv-results-cta-label">
            Your multi-variant inspection workbook is ready.
            {variantCount ? ` (${variantCount} plan sheet${variantCount !== 1 ? 's' : ''})` : ''}
            {variantsError ? ` — ${variantsError}` : ''}
          </p>
          <button className="bpv-view-results-btn" onClick={handleDownload} disabled={downloading}>
            <DownloadIcon />
            {downloading ? 'Downloading…' : 'Download Inspection Plan'}
            {!downloading && <span className="bpv-view-results-arrow">→</span>}
          </button>
        </div>
      )}

      {/* Retry on failure */}
      {isFailed && (
        <div className="bpv-results-cta" style={{ background: '#FFF5F5', borderColor: '#FCA5A5' }}>
          <p className="bpv-results-cta-label" style={{ color: '#B91C1C' }}>
            {failureDetail || 'Tabular scan failed. Please try again.'}
          </p>
          <button className="fps-cancel-all" onClick={onBack} style={{ marginTop: 8 }}>
            ← Back to Upload
          </button>
        </div>
      )}

    </div>
  );
}
